import { yupResolver } from '@hookform/resolvers/yup'
import { ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from 'components/common'
import { DateTime } from 'components/common/input/dateTime'
import toast from 'react-hot-toast'
import * as yup from 'yup'

export type DateRange = {
  from: string
  to: string
}

type Props = {
  title: string
  buttonText: string
  onSubmit: (range: { from: string; to: string }) => void | Promise<void>
  validationSchema?: yup.AnyObjectSchema
  /** Custom submit action rendered below the dates instead of the inline button */
  renderSubmit?: (isSubmitting: boolean) => ReactNode
}

const DateRangeReportForm = ({
  title,
  onSubmit,
  buttonText,
  validationSchema,
  renderSubmit,
}: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DateRange>({
    resolver: yupResolver(validationSchema ?? defaultSchema),
  })

  const handleFormSubmit = async (data: DateRange) => {
    const toastId = toast.loading('Loading...', { duration: Infinity })

    try {
      const from = `${data.from}T00:00:00.000Z`
      const to = `${data.to}T23:59:59.999Z`

      await onSubmit({ from, to })

      toast.success('File will be sent to you in email!', {
        id: toastId,
        duration: 3000,
      })
    } catch (error) {
      toast.error('Something went wrong.', {
        id: toastId,
      })
    }
  }

  return (
    <div>
      <h3 className="title">{title}</h3>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className={`mb-5 flex gap-4 ${
          renderSubmit ? 'flex-col' : 'items-start'
        }`}
      >
        <div className="flex items-start gap-4">
          <DateTime
            required
            label="From"
            name="from"
            register={register}
            error={errors.from?.message}
          />
          <DateTime
            required
            label="To"
            name="to"
            register={register}
            error={errors.to?.message}
          />
        </div>
        {renderSubmit ? (
          renderSubmit(isSubmitting)
        ) : (
          <Button
            className="mt-6 h-max"
            type="submit"
            variant="outline"
            buttonText={buttonText}
          />
        )}
      </form>
    </div>
  )
}

const defaultSchema = yup.object({
  from: yup.string().required('From date is required'),
  to: yup
    .string()
    .required('To date is required')
    .test('is-after-from', 'To must be after From', function (value) {
      const { from } = this.parent
      return new Date(value) >= new Date(from)
    }),
})

export default DateRangeReportForm
